import CancelIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Button,
} from "@mui/material";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { makeStyles } from "@mui/styles";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getLatestPubSLEvent } from "../api/seniority";
import { isUserAuthorizedByServer } from "../api/userAccess";
import PageLoading from "../components/PageLoading";
import SeniorityListDetailTable from "../components/SeniorityListDetailTable";
import SeniorityListDetailTableMobile from "../components/SeniorityListDetailTableMobile";
import UnauthorizedMessage from "../components/UnauthorizedMessage";
import StarsBreadcrumbs from "../components/fundamentals/StarsBreadcrumbs";
import StarsSelectInput from "../components/fundamentals/StarsSelectInput";
import { selectStationFilter, selectUserSession, updateStationFilter } from "../slices/userSession/userSessionSlice";
import { logout } from "../util/authentication";
import { getEmployeeStation, getEmployeeStationList, sortOnCaseSensitiveTexts } from "../util/commonUtils";
import {
  ALL,
  BREADCRUMBS_TITLE,
  CONTRACT_SENIORITY_LIST_TYPE_MAPPER,
  FULL_TIME,
  GET_IAM_SENIORITY_LIST_TYPE_FOR_CLIENT,
  HOME_PATH,
  PART_TIME,
} from "../util/constants";
import { seniorityDataErrorCodeToErrorMessageMap } from "../util/errorMapper";
import { filterSeniorityList } from "../util/filterSeniorityList";
import { validateShiftWorkgroup, validateStation } from "../util/inputValidation";
import { initializeSearchIndex, searchSeniorityList } from "../util/searchSeniorityList";
import { getStationTimeZoneDateTimeStringFromUTCInteger } from "../util/timeZoneService";
import { getUnionContractGivenWorkgroup } from "../util/workgroupMapper.js";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SeniorityListFiltersDialog from "../components/SeniorityListFiltersDialog";
import Typography from "@mui/material/Typography";
import { selectJobCodeData } from "../slices/commonConfig/commonConfigSlice.js";

const SENIORITY_LIST_TABLE_HEIGHT = `calc(85vh - 260px)`;

const useStyles = makeStyles((theme) => ({
  root: {
    margin: "auto",
    position: "fixed",
    width: "100%",
  },
  breadCrumb: {
    marginLeft: "2.3vw",
  },
  desktopTitle: {
    marginLeft: "4.2vw",
  },
  seniorityListFilterAndTitle: {
    minHeight: "170px",
  },
  seniorityListBox: {
    margin: "auto",
    width: "100%",
  },
  seniorityList: {
    width: "92vw",
    marginLeft: "4.25vw",
  },
  textInput: {
    maxWidth: "17vw",
    margin: "auto",
    textAlign: "left",
  },
  stationTextInput: {
    width: "90px",
  },
  workGroupTextInput: {
    width: "180px",
  },
  seniorityListTypeTextInput: {
    width: "200px",
  },
  searchTextInput: {
    width: "250px",
  },
  lastPublished: {
    margin: "5px",
    alignContent: "center",
    marginRight: "3.7vw",
  },
  seniorityInput: {
    margin: "5px",
  },
  seniorityFullPartTimeExport: {
    display: "flex",
    justifyContent: "space-between",
    marginLeft: "4.25vw",
    marginRight: "3.5vw",
    paddingTop: "0.3vw",
    paddingBottom: "0.4vw",
  },
  seniorityInputRow: {
    display: "flex",
    marginLeft: "3.9vw",
    marginTop: "20px",
  },
  toggleButtonGroup: {
    "& .MuiButtonBase-root": {
      color: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      padding: " 6px 14px",
    },
    "& .Mui-selected": {
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
    },
    "& .MuiButtonBase-root:hover": {
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
      opacity: 0.6,
    },
    marginTop: "20px",
  },
  tableHeight: {
    maxHeight: "49vh",
  },
  title: {
    padding: "16px",
    paddingBottom: "8px",
    fontSize: "20px",
    marginLeft: "3px",
  },
  lastPublishedMobile: {
    marginLeft: "20px",
    fontSize: "14px",
  },
  searchInput: {
    margin: "20px",
  },
}));

const EmployeeSeniorityListPage = (props) => {
  const classes = useStyles();

  // Redux State
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);
  const jobCodeData = useSelector(selectJobCodeData);
  const dispatch = useDispatch();

  // Seniority Data State
  const [seniorityList, setSeniorityList] = useState([]);
  const [filteredSeniorityList, setFilteredSeniorityList] = useState([]);
  const [displayedSeniorityList, setDisplayedSeniorityList] = useState([]);

  // UI State
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorObject, setErrorObject] = useState({});

  // Filter State
  const [seniorityListType, setSeniorityListType] = useState("");
  const [station, setStation] = useState("");
  const [stationList, setStationList] = useState([]);
  const [stationErrors, setStationErrors] = useState("");
  const [workgroup, setWorkgroup] = useState("");
  const [workgroupErrors, setWorkgroupErrors] = useState("");
  const [userContract, setUserContract] = useState("");
  const [publishedTimestamp, setPublishedTimestamp] = useState("");

  const [filterSetting, setFilterSetting] = useState(ALL);
  const [searchString, setSearchString] = useState("");
  const [isSearch, setIsSearch] = useState(false);
  const [ptCount, setPtCount] = useState("-");
  const [ftCount, setFtCount] = useState("-");
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);
  const [publishedBy, setPublishedBy] = useState("");
  const isApiSubscribedRef = useRef(true);
  const stationFilterRef = useRef("");
  const workGroupFilterRef = useRef("");
  const seniorityListTypeFilterRef = useRef("");
  const [seniorityRank, setSeniorityRank] = useState(null);
  const apiRef = useGridApiRef();
  const gridRef = React.useRef();
  const employeeBreadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.MY_SENIORITY_LIST];
  const [isFiltersDialogOpen, setIsFiltersDialogOpen] = useState(false);

  const breadcrumbsLink = [HOME_PATH];

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      stationFilterRef.current = stationFilter;
      const station = getEmployeeStation(props.featureConfig.userStations, userSession, stationFilter);
      let userWorkgroup;
      let userUnionContract;
      userWorkgroup = jobCodeData[userSession.jobCode].workgroup;
      userUnionContract = jobCodeData[userSession.jobCode].unionContract;
      const updatedSeniorityListType = CONTRACT_SENIORITY_LIST_TYPE_MAPPER(userUnionContract, station)[0];
      setStation(station);
      setWorkgroup(userWorkgroup);
      setUserContract(userUnionContract);
      setStationList(getEmployeeStationList(userSession));
      dispatch(updateStationFilter(station));
      setSeniorityListType(updatedSeniorityListType);
      if (userWorkgroup !== "" && updatedSeniorityListType !== "") {
        updateSeniorityList(station, userWorkgroup, updatedSeniorityListType, userUnionContract);
      }
    }
    return () => {
      isApiSubscribedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState]);

  useEffect(() => {
    initializeSearchIndex(filteredSeniorityList);
    const newSearchStringTrimmed = searchString.trim().toLowerCase();

    if (isSearch) {
      searchSeniorityList({
        searchString: newSearchStringTrimmed,
        applySearchCallback: applySearch,
        contract: userContract,
        workgroup,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredSeniorityList]);

  useEffect(() => {
    setSearchString("");
  }, [station, workgroup, seniorityListType]);

  const doesResponseQueryParamsMatchCurrentFilters = (responseQueryParams) => {
    return (
      responseQueryParams.station === stationFilterRef.current &&
      responseQueryParams.workgroup === workGroupFilterRef.current &&
      (GET_IAM_SENIORITY_LIST_TYPE_FOR_CLIENT[responseQueryParams.seniorityListType] ===
        seniorityListTypeFilterRef.current ||
        responseQueryParams.seniorityListType === seniorityListTypeFilterRef.current)
    );
  };

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(true);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  const updateSeniorityList = (station, workgroup, seniorityListType, contract) => {
    isApiSubscribedRef.current = true;
    stationFilterRef.current = station;
    workGroupFilterRef.current = workgroup;
    seniorityListTypeFilterRef.current = seniorityListType;
    setIsLoading(true);
    setIsError(false);
    props.isDesktopView && setFilterSetting(ALL);
    setErrorObject({});
    setSeniorityList([]);
    setDisplayedSeniorityList([]);
    setFilteredSeniorityList([]);
    setFtCount("-");
    setPtCount("-");
    setSeniorityRank(null);
    setPublishedTimestamp(null);
    setPublishedBy("");
    getLatestPubSLEvent(station, workgroup, seniorityListType)
      .then((res) => res.json())
      .then((result) => {
        if (isApiSubscribedRef.current && doesResponseQueryParamsMatchCurrentFilters(result.queryParams)) {
          if (result.message) {
            console.error("API response came back with a message: ", result.message);
            throw new Error("Unexpected error");
          } else {
            const rank = result?.seniorityList.findIndex((item) => item.eid === userSession.eid);
            if (rank !== -1) {
              setSeniorityList(result?.seniorityList);
              const filteredSeniorityList = props.isDesktopView
                ? filterSeniorityList(result?.seniorityList, ALL)
                : filterSeniorityList(result?.seniorityList, filterSetting);
              setFilteredSeniorityList(filteredSeniorityList);
              setDisplayedSeniorityList(filteredSeniorityList);
              setPublishedTimestamp(parseInt(result?.createdAt));
              setSeniorityRank(rank + 1);
              setPtCount(result.partTimeCount);
              setFtCount(result.fullTimeCount);
              setIsLoading(false);
              setPublishedBy(result?.publishedBy);
            } else {
              setPtCount(0);
              setFtCount(0);
              setIsLoading(false);
            }
          }
        }
      })
      .catch((err) => {
        console.error("error happened!", err);
        setIsLoading(false);
        setIsError(true);
        setErrorObject(seniorityDataErrorCodeToErrorMessageMap.unexpectedSeniorityDataError);
      });
  };

  const handleSeniorityListTypeChange = (event) => {
    setSeniorityListType(event.target.value);
    seniorityListTypeFilterRef.current = event.target.value;
    props.isDesktopView && updateSeniorityList(station, workgroup, event.target.value, userContract);
  };

  const handleResetClick = () => {
    setFilterSetting(ALL);
    setSeniorityListType(CONTRACT_SENIORITY_LIST_TYPE_MAPPER(userContract, station)[0]);
  };
  const handleApplyFilters = () => {
    setIsFiltersDialogOpen(false);
    updateSeniorityList(station, workgroup, seniorityListType, getUnionContractGivenWorkgroup(workgroup));
  };
  const handleStationChange = (event) => {
    setStation(event.target.value);
    stationFilterRef.current = event.target.value;
    dispatch(updateStationFilter(event.target.value));
    const updatedSeniorityListType = CONTRACT_SENIORITY_LIST_TYPE_MAPPER(userContract, event.target.value)[0];
    setSeniorityListType(updatedSeniorityListType);
    if (workgroup !== "" && updatedSeniorityListType !== "" && props.isDesktopView) {
      updateSeniorityList(
        event.target.value,
        workGroupFilterRef.current,
        updatedSeniorityListType,
        getUnionContractGivenWorkgroup(workGroupFilterRef.current)
      );
    }
  };

  const handleStationBlur = (event) => {
    setStationErrors(validateStation(event.target.value));
  };

  const handleFilterSettingChange = (event, newValue) => {
    if (newValue !== null) {
      setFilterSetting(newValue);
      let filteredList = filterSeniorityList(seniorityList, newValue);
      setFilteredSeniorityList(filteredList);
      setDisplayedSeniorityList(filteredList);
    }
  };
  const handleMobileFilterSettingChange = (event) => {
    const newValue = event.target.value;
    if (newValue !== null) {
      setFilterSetting(newValue);
    }
  };
  const handleWorkgroupSelectBlur = (event) => {
    setWorkgroupErrors(validateShiftWorkgroup(event.target.value));
  };

  const handleSearchChange = (event) => {
    setSearchString(event.target.value);
  };

  const handleSearchBlur = (event) => {
    if (event.target.value.trim() === "") {
      handleSearchSubmit("");
    }
  };

  const handleSearchEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleSearchSubmit(event.target.value);
    }
  };

  const handleSearchCancel = () => {
    setSearchString("");
    handleSearchSubmit("");
  };

  const handleSearchSubmit = (newSearchString) => {
    const newSearchStringTrimmed = newSearchString.trim().toLowerCase();
    searchSeniorityList({
      searchString: newSearchStringTrimmed,
      applySearchCallback: applySearch,
      contract: userContract,
      workgroup,
    });
  };

  const applySearch = (searchString, searchedSeniorityList) => {
    if (searchString === "") {
      setIsSearch(false);
    } else {
      setIsSearch(true);
    }
    setDisplayedSeniorityList(searchedSeniorityList);
  };

  const handleRankClick = () => {
    if (seniorityRank !== -1) {
      // Wait for the scroll to complete, then scroll to the item
      let sortModel = apiRef.current.getSortModel();
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: sortModel[0].sort === "asc" ? seniorityRank - 1 : seniorityList.length - seniorityRank,
        });
      }, 0);
      // Scroll item to the top, we can scroll using data-id or data-rowindex that datagrid is providing
      setTimeout(() => {
        const rowElem = document.querySelector(`[data-id="${seniorityRank}"]`);
        if (rowElem) {
          rowElem.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 50); // Delay 50 to find data-id in the DOM to load after scrollToIndexes call
    }
  };
  const renderSeniorityListTypeItem = (seniorityListTypeString) => {
    return (
      <MenuItem key={seniorityListTypeString} value={seniorityListTypeString}>
        {seniorityListTypeString}
      </MenuItem>
    );
  };

  const renderSeniorityList = (seniorityList) => (
    <SeniorityListDetailTable
      apiRef={apiRef}
      seniorityList={seniorityList}
      seniorityRank={seniorityRank}
      contract={getUnionContractGivenWorkgroup(workGroupFilterRef.current)}
      workgroup={workgroup}
      seniorityListType={seniorityListTypeFilterRef.current}
      station={stationFilterRef.current}
      isLoading={isLoading}
      isError={isError}
      errorObject={errorObject}
      seniorityListTableHeight={SENIORITY_LIST_TABLE_HEIGHT}
      isEmployeeView={true}
      seniorityListFtPtCount={ptCount + ftCount}
      gridRef={gridRef}
    />
  );
  const renderSeniorityListMobile = () => (
    <SeniorityListDetailTableMobile
      apiRef={apiRef}
      seniorityList={displayedSeniorityList}
      seniorityRank={seniorityRank}
      contract={userContract}
      workgroup={workgroup}
      seniorityListType={seniorityListTypeFilterRef.current}
      station={stationFilterRef.current}
      isLoading={isLoading}
      isError={isError}
      errorObject={errorObject}
      isEmployeeView={true}
      seniorityListFtPtCount={ptCount + ftCount}
      gridRef={gridRef}
      publishedBy={publishedBy}
      publishedTimestamp={publishedTimestamp}
      handleSearchEnterKeyPressed={handleSearchEnterKeyPressed}
      handleSearchChange={handleSearchChange}
      handleSearchBlur={handleSearchBlur}
      searchString={searchString}
      handleSearchCancel={handleSearchCancel}
      handleSearchSubmit={handleSearchSubmit}
      handleRankClick={handleRankClick}
      stationList={stationList}
    />
  );
  const SearchButton = () => {
    return (
      <IconButton size="small" className="search-icon-button" onMouseDown={() => handleSearchSubmit(searchString)}>
        <SearchIcon />
      </IconButton>
    );
  };

  const CancelButton = () => {
    return (
      <IconButton size="small" className="cancel-icon-button" onMouseDown={handleSearchCancel}>
        <CancelIcon />
      </IconButton>
    );
  };
  const handleFilterClick = () => {
    setIsFiltersDialogOpen(true);
  };
  const renderEmployeeMobileSeniorityListPage = () => {
    return (
      <div data-testid="employee-seniority-list-mobile-page">
        <Grid container textAlign="left">
          <Grid item md={12} xs={12} sm={12}>
            <div className={classes.title}>My Seniority List</div>
          </Grid>
          <Grid item md={12} xs={12} sm={12}>
            <div
              className={classes.lastPublishedMobile}
              hidden={isLoading || publishedTimestamp === "" || seniorityList.length === 0}
            >
              Last published by {publishedBy}
              {!isLoading && publishedTimestamp !== ""
                ? " on " +
                  getStationTimeZoneDateTimeStringFromUTCInteger(publishedTimestamp, station).toFormat(
                    "MM/dd/yyyy T ZZZZ"
                  )
                : ""}
            </div>
          </Grid>
        </Grid>
        <div className={classes.searchInput}>
          <TextField
            label="Search"
            variant="outlined"
            value={searchString}
            data-testid="employee-mobile-search"
            onChange={handleSearchChange}
            onBlur={handleSearchBlur}
            fullWidth
            disabled={!workgroup}
            InputProps={{
              endAdornment:
                searchString !== "" ? (
                  <InputAdornment position="end">
                    <CancelButton />
                    <SearchButton />
                  </InputAdornment>
                ) : (
                  <InputAdornment position="end">
                    <SearchButton />
                  </InputAdornment>
                ),
            }}
            onKeyUp={handleSearchEnterKeyPressed}
          />
        </div>
        <Grid container>
          <Box
            sx={{
              bgcolor: "secondary.swaLightestBlue",
              width: "100%",
              height: "60px",
              borderTopLeftRadius: "5px",
              borderTopRightRadius: "5px",
              borderTop: "solid 1px",
              borderLeft: "solid 1px",
              borderRight: "solid 1px",
              borderColor: "secondary.swaGray2",
              borderRadius: 0,
            }}
          >
            <Stack
              sx={{ height: "100%", justifyContent: "left", alignItems: "center", padding: "15px" }}
              direction="row"
              divider={<Divider sx={{ marginTop: "5px", marginBottom: "5px" }} orientation="vertical" flexItem />}
            >
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid item xs={8} sm={8}>
                  <Box
                    sx={{
                      textAlign: "left",
                    }}
                    hidden={!seniorityList.length}
                  >
                    <Link component="button" variant="body2" onClick={handleRankClick}>
                      <b>{"You are rank " + seniorityRank + " of " + seniorityList.length}</b>
                    </Link>
                  </Box>
                </Grid>
                <Grid item xs={4} sm={4}>
                  <Button
                    style={{ backgroundColor: "white", paddingLeft: 8, paddingRight: 8 }}
                    size="small"
                    fullWidth
                    onClick={handleFilterClick}
                  >
                    <Grid
                      container
                      spacing={1}
                      justifyContent="space-between"
                      alignItems="center"
                      alignContent="center"
                    >
                      <Grid item alignItems="center" xs={3} sm={2} md={1}>
                        <FilterAltIcon sx={{ marginTop: 1, fontSize: "18px" }} />
                      </Grid>
                      <Grid item xs={9} sm={10} md={11}>
                        <Typography sx={{ fontSize: "14px" }}>Filters</Typography>
                      </Grid>
                    </Grid>
                  </Button>
                </Grid>
              </Grid>
            </Stack>
          </Box>
        </Grid>
        <Box
          sx={{
            height: "60vh",
            width: "100%",
          }}
        >
          <div
            style={{
              width: "100%",
              height: "inherit",
            }}
          >
            {renderSeniorityListMobile(props)}
          </div>
        </Box>
        {isFiltersDialogOpen && (
          <SeniorityListFiltersDialog
            isDialogOpen={isFiltersDialogOpen}
            setIsDialogOpen={setIsFiltersDialogOpen}
            station={station}
            stationList={stationList}
            seniorityList={seniorityList}
            contract={userContract}
            workgroup={workgroup}
            seniorityListType={seniorityListType}
            handleStationChange={handleStationChange}
            handleStationBlur={handleStationBlur}
            stationErrors={stationErrors}
            handleSeniorityListTypeChange={handleSeniorityListTypeChange}
            handleFtPtFilterChange={handleMobileFilterSettingChange}
            filterSetting={filterSetting}
            handleResetClick={handleResetClick}
            handleApplyFilters={handleApplyFilters}
            handleWorkgroupSelectBlur={handleWorkgroupSelectBlur}
            workgroupErrors={workgroupErrors}
          />
        )}
      </div>
    );
  };

  const renderEmployeeDesktopSeniorityListPage = () => {
    return (
      <div>
        <div className={classes.breadCrumb}>
          <StarsBreadcrumbs
            isConfirmationRequired={false}
            breadcrumbsLinks={breadcrumbsLink}
            breadcrumbsList={employeeBreadcrumbsList}
          />
        </div>
        <div className={classes.root}>
          <div className={classes.seniorityListFilterAndTitle}>
            <div
              className={
                !props.isDesktopView
                  ? `${classes.pageTitle} page-title-left`
                  : `page-title-left ${classes.desktopTitle}`
              }
            >
              {BREADCRUMBS_TITLE.MY_SENIORITY_LIST}
            </div>
            <div className={classes.seniorityInputRow}>
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid item container justifyContent="flex-start" md={8} lg={8}>
                  <div className={classes.seniorityInput} data-test-id="employeeStationSelectElement">
                    {stationList.length > 1 ? (
                      <StarsSelectInput
                        label="Station"
                        menuMessage="Station"
                        value={stationFilter}
                        allOptions={stationList}
                        sortComparator={sortOnCaseSensitiveTexts}
                        onChange={handleStationChange}
                        onBlur={handleStationBlur}
                        errors={stationErrors}
                        multiple={false}
                      />
                    ) : (
                      <TextField
                        className={
                          props.isDesktopView
                            ? `${classes.textInput} ${classes.stationTextInput}`
                            : classes.stationTextField
                        }
                        label="Station"
                        variant="outlined"
                        data-testid="employeeStationSelectElement"
                        value={station}
                        onChange={handleStationChange}
                        onBlur={handleStationBlur}
                        disabled
                        error={stationErrors !== ""}
                        helperText={stationErrors}
                      />
                    )}
                  </div>
                  <div className={classes.seniorityInput}>
                    <TextField
                      id="workgroupSelectElement"
                      data-testid="employee-workgroup-select"
                      fullWidth
                      label="Workgroup"
                      variant="outlined"
                      value={workgroup}
                      onBlur={handleWorkgroupSelectBlur}
                      disabled
                      error={!!(workgroupErrors !== "")}
                      helperText={workgroupErrors}
                    />
                  </div>
                  <div className={classes.seniorityInput}>
                    <FormControl variant="outlined" className={classes.selectInput}>
                      <InputLabel>Seniority List Type</InputLabel>
                      <Select
                        id="seniorityListTypeSelectElement"
                        data-testid="employee-seniorityListType-select"
                        className={`${classes.textInput} ${classes.seniorityListTypeTextInput}`}
                        value={seniorityListType}
                        onChange={handleSeniorityListTypeChange}
                        label="Seniority List Type"
                        disabled={!workgroup}
                      >
                        <MenuItem value="" disabled>
                          <em>Seniority List Type</em>
                        </MenuItem>
                        {userContract !== "" &&
                          CONTRACT_SENIORITY_LIST_TYPE_MAPPER(userContract, station)
                            .slice()
                            .sort()
                            .map((seniorityListType) => {
                              return renderSeniorityListTypeItem(seniorityListType);
                            })}
                      </Select>
                    </FormControl>
                  </div>
                  <div className={classes.seniorityInput}>
                    <TextField
                      id="employeeSearchElement"
                      className={`${classes.textInput} ${classes.searchTextInput}`}
                      data-testid="employeeSearchElement"
                      label="Search"
                      variant="outlined"
                      value={searchString}
                      onChange={handleSearchChange}
                      onBlur={handleSearchBlur}
                      disabled={!workgroup}
                      InputProps={{
                        endAdornment:
                          searchString !== "" ? (
                            <InputAdornment position="end">
                              <CancelButton />
                              <SearchButton />
                            </InputAdornment>
                          ) : (
                            <InputAdornment position="end">
                              <SearchButton />
                            </InputAdornment>
                          ),
                      }}
                      onKeyUp={handleSearchEnterKeyPressed}
                    />
                  </div>
                </Grid>
                <Grid item container justifyContent="flex-end" md={4} lg={4}>
                  <div
                    hidden={isLoading || publishedTimestamp === "" || seniorityList.length === 0}
                    className={classes.lastPublished}
                  >
                    Last published by {publishedBy}
                    {!isLoading && publishedTimestamp !== ""
                      ? " on " +
                        getStationTimeZoneDateTimeStringFromUTCInteger(publishedTimestamp, station).toFormat(
                          "MM/dd/yyyy T ZZZZ"
                        )
                      : ""}
                  </div>
                </Grid>
              </Grid>
            </div>

            <div className={`${classes.seniorityInput} ${classes.seniorityFullPartTimeExport}`}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={filterSetting}
                onChange={handleFilterSettingChange}
                color={"primary"}
                className={classes.toggleButtonGroup}
              >
                <ToggleButton value={FULL_TIME}>FT {ftCount !== "" ? "(" + ftCount + ")" : ""}</ToggleButton>
                <ToggleButton value={PART_TIME}>PT {ptCount !== "" ? "(" + ptCount + ")" : ""}</ToggleButton>
                <ToggleButton value={ALL}>
                  ALL {ftCount !== "" ? "(" + (ftCount === "-" ? ftCount : ftCount + ptCount) + ")" : ""}
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
          </div>
          <div className={classes.seniorityListBox}>
            <Grid container className={classes.seniorityList}>
              <Box
                hidden={!displayedSeniorityList.length}
                sx={{
                  bgcolor: "secondary.swaLightestBlue",
                  width: "100%",
                  height: "50px",
                  borderTopLeftRadius: "5px",
                  borderTopRightRadius: "5px",
                  borderTop: "solid 1px",
                  borderLeft: "solid 1px",
                  borderRight: "solid 1px",
                  borderColor: "secondary.swaGray2",
                }}
              >
                <Stack
                  sx={{ height: "100%", justifyContent: "left", alignItems: "center" }}
                  direction="row"
                  divider={<Divider sx={{ marginTop: "5px", marginBottom: "5px" }} orientation="vertical" flexItem />}
                >
                  <Box
                    sx={{
                      marginLeft: "13px",
                    }}
                  >
                    <Link component="button" variant="body2" onClick={handleRankClick}>
                      <b>{"You are rank " + seniorityRank + " of " + seniorityList.length}</b>
                    </Link>
                  </Box>
                </Stack>
              </Box>
            </Grid>
            <Box
              sx={{
                height: SENIORITY_LIST_TABLE_HEIGHT,
              }}
            >
              {renderSeniorityList(displayedSeniorityList)}
            </Box>
          </div>
        </div>
      </div>
    );
  };

  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }

  return (
    <div data-testid="employee-seniority-list-page">
      {isUserAuthorizedStateLoading ? (
        <PageLoading />
      ) : (
        <>{props.isDesktopView ? renderEmployeeDesktopSeniorityListPage() : renderEmployeeMobileSeniorityListPage()}</>
      )}
    </div>
  );
};

export default EmployeeSeniorityListPage;
